import { UploaderFooter } from "@components/Footer/UploaderFooter";
import { EyeIcon } from "@components/Icons/EyeIcon";
import { ImageIcon } from "@components/Icons/Image";
import { VideoIcon } from "@components/Icons/Video";
import { MultiPicker } from "@components/ImagePicker";
import { Loading } from "@components/Loading";
import { Pictures } from "@components/Pictures";
import { TopNav } from "@components/TopNav";
import ReactVideoRecorder from "@components/VideoRecorder/video-recorder";
import { extractFrameFromVideoBlob } from "@helpers/extractFrameFromVideoBlob";
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from "@helpers/fileLimits";
import { useEventNavigate } from "@helpers/useEventNavigate";
import { getExtraTimestamp } from "@helpers/extrasMapHelper";
import { useTranslation } from "@helpers/useTranslation";
import { Timestamp, doc, getDoc, updateDoc, writeBatch } from "firebase/firestore";
import { ref as storageRef, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "src/helpers/firebase";
import { uid } from "src/helpers/uid";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useTeam } from "src/helpers/useTeam";
import { useDismissableToast, useToast } from "src/helpers/useToast";

const ALLOWED_MAX_EXTRA_IMAGES = 500;

type UploadMode = "image" | "video" | "preview";

export const Uploader = () => {
  const { t } = useTranslation("upload");
  const { storage, firestore } = useContext(FirebaseContext);
  const [team] = useTeam();
  const eventId = useEventId();
  const [event] = useEvent();
  const [uploading, setUploading] = useState(false);
  const toast = useToast();
  const { showToast, dismiss } = useDismissableToast();
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalUploadCount, setTotalUploadCount] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>("image");
  const [videos, setVideos] = useState<Blob[]>([]);
  const [frames, setFrames] = useState<Blob[]>([]);
  const [newUploadsCount, setNewUploadsCount] = useState(0);
  const [hideManualUpload, setHideManualUpload] = useState(false);

  const sortByFirebaseTimestamp = (a: string, b: string) => {
    const aEntry = team?.extrasMap[a];
    const bEntry = team?.extrasMap[b];
    if (!aEntry || !bEntry) return 0;

    // Handle both legacy (Timestamp) and new format (ExtraMapEntry)
    const aTimestamp = getExtraTimestamp(aEntry);
    const bTimestamp = getExtraTimestamp(bEntry);

    return bTimestamp.seconds - aTimestamp.seconds;
  };

  const extras = useMemo(() => Object.keys(team?.extrasMap || {}).sort(sortByFirebaseTimestamp), [team?.extrasMap]);
  const numberOfExtraPics = extras.length;

  const handleVideoInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const processVideo = async (file: File) => {
        if (file.size > MAX_VIDEO_FILE_SIZE) {
          toast(t("videoFileTooLarge"), "🤷‍♂️", "error");
          console.error(`Video file too large. Compressed Video. Event: ${eventId}`);
          return null;
        }

        try {
          const videoBlob = new Blob([file], { type: file.type });
          const frame = await extractFrameFromVideoBlob(videoBlob, 1);
          return { video: videoBlob, frame };
        } catch (error) {
          console.error(error);
          toast(t("errorProcessingVideo"), "🤷‍♂️", "error");
          return null;
        }
      };

      // Process videos sequentially to prevent overwhelming the browser
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const result = await processVideo(files[i]);
        results.push(result);
      }

      const validResults = results.filter((result): result is { video: Blob; frame: Blob } => result !== null);

      setVideos(validResults.map((result) => result.video));
      setFrames(validResults.map((result) => result.frame));
    }
  };

  const upload = async (newImages: File[]) => {
    setUploading(true);
    if (newImages.length > ALLOWED_MAX_EXTRA_IMAGES) {
      toast(`${t("tooManyImages")}${ALLOWED_MAX_EXTRA_IMAGES}`, "🤷‍♂️", "error");
      console.error(`Too many images. Event: ${eventId}, Team: ${team?.id}`);
      newImages = newImages.slice(0, ALLOWED_MAX_EXTRA_IMAGES);
    }

    try {
      if (!team) throw new Error(`Team not found. Event: ${eventId}, Team: ${team?.id}`);

      setTotalUploadCount(newImages.length);
      const batch = writeBatch(firestore);

      const extrasIds = [];
      for (const newImage of newImages) {
        if (newImage.size > MAX_IMAGE_FILE_SIZE) {
          toast(t("fileTooLarge"), "🤷‍♂️", "error");
          console.error(`File too large. Compressed Image. Event: ${eventId}, Team: ${team?.id}`);
          continue;
        }

        const id = uid();
        const imagePath = `events/${eventId}/teams/${team.id}/extras/${id}`;
        const imageRef = storageRef(storage, imagePath);
        try {
          await uploadBytes(imageRef, newImage);
        } catch (error) {
          console.error(error);
          continue;
        }

        setUploadedCount((prev) => prev + 1);
        extrasIds.push(id);

        const docRef = doc(firestore, `events/${eventId}/teams/${team.id}/extras/${id}`);
        batch.set(docRef, { createdOn: Timestamp.now() });
      }
      setUploaded(true);

      try {
        const teamDocRef = doc(firestore, `events/${eventId}/teams/${team.id}`);
        const teamDoc = await getDoc(teamDocRef);
        const teamData = teamDoc.data();
        const extrasMap = teamData?.extrasMap || {};

        for (const id of extrasIds) {
          if (extrasMap[id]) continue;
          extrasMap[id] = Timestamp.now();
        }
        await updateDoc(teamDocRef, { extrasMap });
      } catch (error) {
        console.error("!WORK REQUIRED! Batch update failed and will need to be retried manually.");
        console.error(error);
        if (error.message.includes("PERMISSION_DENIED")) toast(t("sessionExpired"), "🤷‍♂️", "error");
        else toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      }

      batch
        .commit()
        .then(() => {
          toast(t("uploaded"), "✅");
          setNewUploadsCount((prev) => prev + extrasIds.length);
        })
        .catch((error) => {
          console.error("!WORK REQUIRED! Batch update failed and will need to be retried manually.");
          console.error(error);
          if (error.message.includes("PERMISSION_DENIED")) toast(t("sessionExpired"), "🤷‍♂️", "error");
          else toast(t("somethingWentWrong"), "🤷‍♂️", "error");
        });
    } catch (error) {
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      setUploading(false);
      setUploadedCount(1);
      setTotalUploadCount(0);
    }
  };

  const uploadVideo = async () => {
    if (videos.length === 0 || frames.length === 0 || !team) return;

    setUploading(true);
    setTotalUploadCount(videos.length);
    setUploadedCount(0);

    try {
      const batch = writeBatch(firestore);
      const teamDocRef = doc(firestore, `events/${eventId}/teams/${team.id}`);
      const teamDoc = await getDoc(teamDocRef);
      const teamData = teamDoc.data();
      const extrasMap = teamData?.extrasMap || {};

      const uploadSingleVideo = async (video: Blob, frame: Blob, index: number) => {
        const id = uid();
        const videoPath = `events/${eventId}/teams/${team.id}/extras/${id}.mp4`;
        const framePath = `events/${eventId}/teams/${team.id}/extras/${id}`;

        const videoRef = storageRef(storage, videoPath);
        const frameRef = storageRef(storage, framePath);

        await uploadBytesResumable(videoRef, video);
        await uploadBytes(frameRef, frame);

        const docRef = doc(firestore, `events/${eventId}/teams/${team.id}/extras/${id}`);
        batch.set(docRef, {
          createdOn: Timestamp.now(),
          hasVideo: true,
        });

        extrasMap[id] = Timestamp.now();
        extrasMap[`${id}-video`] = Timestamp.now();

        setUploadedCount(index + 1);
        return id;
      };

      // Upload videos sequentially to prevent network congestion
      const threeMinutes = 60000 * 3;
      showToast(t("videoUploading"), "⏳", "loading", threeMinutes);
      for (let i = 0; i < videos.length; i++) {
        await uploadSingleVideo(videos[i], frames[i], i);
      }
      dismiss();

      await updateDoc(teamDocRef, { extrasMap });
      await batch.commit();

      toast(t("uploaded"), "✅");
      setUploaded(true);
      setNewUploadsCount((prev) => prev + videos.length);
      setVideos([]);
      setFrames([]);
    } catch (error) {
      console.error(error);
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
    } finally {
      setUploading(false);
      setUploadedCount(0);
      setTotalUploadCount(0);
    }
  };

  const navigate = useEventNavigate();
  const globalNavigate = useNavigate();
  const showUpload = numberOfExtraPics < ALLOWED_MAX_EXTRA_IMAGES;
  const forceRefreshPage = () => window.location.reload();

  // const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9") ? "video/webm;codecs=vp9" : "video/mp4";
  const mimeType = "video/mp4";

  const backToGalleryLink = event?.galleryPublished && event?.shareUuid ? `/gallery/${event?.shareUuid}` : null;

  return (
    <div className="flex flex-col">
      <TopNav isUploader={true} />
      <main className="flex-col">
        <div className="flex flex-col items-center">
          <div className="w-full bg-base-200 bg-opacity-50">
            <h2 className="mb-4 mt-8 text-center text-lg font-bold">{t("title")}</h2>
            {showUpload && (
              <div className="mb-8 mt-8 flex justify-center">
                <div className="flex gap-4">
                  <button
                    role="tab"
                    className={`flex h-24 w-24 flex-col items-center justify-center rounded-xl border transition-colors duration-150
                    ${
                      uploadMode === "image"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-base-200 bg-base-100 text-base-content/60 hover:bg-base-200"
                    }
                  `}
                    onClick={() => setUploadMode("image")}
                  >
                    <ImageIcon
                      className={`mb-1 h-8 w-8 ${uploadMode === "image" ? "text-primary" : "text-base-content/60"}`}
                    />
                    <span
                      className={`whitespace-pre-line text-center text-xs font-medium ${
                        uploadMode === "image" ? "text-primary" : "text-base-content/50"
                      }`}
                    >
                      {t("uploadImages")}
                    </span>
                  </button>
                  <button
                    role="tab"
                    className={`flex h-24 w-24 flex-col items-center justify-center rounded-xl border transition-colors duration-150
                    ${
                      uploadMode === "video"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-base-200 bg-base-100 text-base-content/60 hover:bg-base-200"
                    }
                  `}
                    onClick={() => setUploadMode("video")}
                  >
                    <VideoIcon
                      className={`mb-1 h-8 w-8 ${uploadMode === "video" ? "text-primary" : "text-base-content/60"}`}
                    />
                    <span
                      className={`break-words text-center text-xs font-medium ${
                        uploadMode === "video" ? "text-primary" : "text-base-content/50"
                      }`}
                    >
                      {t("uploadVideo")}
                    </span>
                  </button>
                  <button
                    role="tab"
                    className={`flex h-24 w-24 flex-col items-center justify-center rounded-xl border transition-colors duration-150
                    ${
                      uploadMode === "preview"
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-base-200 bg-base-100 text-base-content/60 hover:bg-base-200"
                    }
                  `}
                    onClick={() => {
                      setUploadMode("preview");
                      setNewUploadsCount(0);
                    }}
                  >
                    <div className="relative">
                      <EyeIcon
                        className={`mb-1 h-8 w-8 ${uploadMode === "preview" ? "text-primary" : "text-base-content/60"}`}
                      />
                      {newUploadsCount > 0 && (
                        <div
                          className={`absolute -top-8 -right-10 flex items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white ${
                            newUploadsCount > 9 ? "h-8 w-8" : "h-6 w-6"
                          }`}
                        >
                          +{newUploadsCount}
                        </div>
                      )}
                    </div>
                    <span
                      className={`break-words text-center text-xs font-medium ${
                        uploadMode === "preview" ? "text-primary" : "text-base-content/50"
                      }`}
                    >
                      {t("photos")}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {backToGalleryLink && (
              <div className="mb-8 flex justify-center text-center">
                <button className={`text-xs text-neutral underline`} onClick={() => globalNavigate(backToGalleryLink)}>
                  {t("backToGallery")}
                </button>
              </div>
            )}

            {!uploading && !event?.mainConfig?.uploadersOnly && (
              <div className="mb-8 flex justify-center text-center">
                <button className={`text-xs text-neutral underline`} onClick={() => navigate("/teams")}>
                  {t("play")}
                </button>
              </div>
            )}
          </div>

          <div className="mt-2 flex w-full flex-col items-center p-4">
            {showUpload && uploadMode === "image" && (
              <div className="mt-24 w-full">
                <MultiPicker
                  uploading={uploading}
                  uploadLabel={t("uploadButton")}
                  onNewImages={upload}
                  setUploading={setUploading}
                  setTotalUploadCount={setTotalUploadCount}
                />
                <div className="mt-2 text-center">
                  <a
                    onClick={() => {
                      setUploadMode("preview");
                      setNewUploadsCount(0);
                    }}
                    className="text-xs text-slate-500 underline hover:cursor-pointer"
                  >
                    {t("previewExistingUploads")}
                  </a>
                </div>
              </div>
            )}

            {showUpload && uploadMode === "video" && (
              <div className="mt-12 w-full max-w-lg">
                <div className="h-[50vh] w-full">
                  <ReactVideoRecorder
                    mimeType={mimeType}
                    uploading={uploading}
                    showReplayControls={true}
                    t={t}
                    onError={(error) => {
                      console.error(error);
                      setHideManualUpload(true);
                    }}
                    renderErrorView={() => (
                      <div className="flex w-full flex-col justify-center">
                        <div className="mx-8 flex flex-col justify-center">
                          <div className="mb-6 text-lg">{t("uploadVideoFromDevice")}</div>
                          <div className="mb-12 text-xs text-gray-300">{t("uploadVideoNote")}</div>
                          <input
                            onChange={handleVideoInput}
                            type="file"
                            accept="video/*"
                            multiple
                            className="rounded-lg border border-primary bg-white p-3 text-sm text-primary"
                          />
                        </div>
                        <div className="mt-6 flex justify-center">
                          <button
                            disabled={videos.length === 0 || uploading}
                            onClick={uploadVideo}
                            className={`btn-primary btn ${uploading ? "loading" : ""} ${
                              videos.length > 0 ? "" : "invisible"
                            }`}
                          >
                            {t("upload")} {videos.length > 1 ? `(${videos.length})` : ""}
                          </button>
                        </div>
                      </div>
                    )}
                    timeLimit={60000}
                    isFlipped={false}
                    constraints={{
                      audio: true,
                    }}
                    renderDisconnectedView={() => <></>}
                    onStartRecording={() => setUploaded(false)}
                    onRecordingComplete={async (videoBlob: Blob) => {
                      const frame = await extractFrameFromVideoBlob(videoBlob, 1);
                      setVideos([videoBlob]);
                      setFrames([frame]);
                    }}
                    onUpload={uploadVideo}
                    uploaded={uploaded}
                    showSubmitButton
                  />
                </div>
                <div className={`${hideManualUpload ? "hidden" : ""}`}>
                  <div className="flex w-full flex-col justify-center">
                    <div className="mx-8 flex flex-col justify-center">
                      <div className="text-md pt-10 pb-2 text-center">{t("uploadExistingVideo")}</div>
                      <input
                        onChange={handleVideoInput}
                        type="file"
                        accept="video/*"
                        multiple
                        className="rounded-lg border border-primary bg-white p-3 text-sm text-primary"
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-center">
                    <button
                      disabled={videos.length === 0 || uploading}
                      onClick={uploadVideo}
                      className={`btn-primary btn ${uploading ? "loading" : ""} ${
                        videos.length > 0 ? "" : "invisible"
                      }`}
                    >
                      {t("upload")} {videos.length > 1 ? `(${videos.length})` : ""}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showUpload && uploadMode === "preview" && (
              <Loading loading={!team}>
                <Pictures
                  challenges={[]}
                  teams={team ? [team] : []}
                  eventId={eventId}
                  allowDownload={true}
                  allowDelete={true}
                />
                {uploaded && (
                  <div className="mt-16 mb-12 flex flex-col justify-center text-center">
                    <div className="mb-2 w-full text-sm font-bold">{t("cantSeeAllPictures")}</div>
                    <button className={`text-xs text-neutral text-gray-500 underline`} onClick={() => forceRefreshPage}>
                      {t("refresh")}
                    </button>
                  </div>
                )}
              </Loading>
            )}
          </div>

          {uploading && (
            <p className="mb-12 text-center text-sm font-bold">
              {`${t("uploading")}... ${uploadedCount} / ${totalUploadCount}`}
            </p>
          )}

          <UploaderFooter />
        </div>
      </main>
    </div>
  );
};
